import { useCallback, useEffect, useState } from 'react'
import { deleteMemoryFact, listMemoryFacts, purgeWorkspaceMemory } from '../services/memorySettingsApi'
import type { MemoryFact } from '../types'

export function useMemoryFacts(workspaceId: string) {
  const [facts, setFacts] = useState<MemoryFact[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({})
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})
  const [purging, setPurging] = useState(false)
  const [purgeError, setPurgeError] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listMemoryFacts(workspaceId)
      .then((page) => {
        if (cancelled) return
        setFacts(page.facts)
        setCursor(page.nextCursor)
        setHasMore(page.nextCursor !== null)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load remembered facts')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [workspaceId])

  useEffect(() => load(), [load])

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    listMemoryFacts(workspaceId, { cursor })
      .then((page) => {
        setFacts((prev) => [...prev, ...page.facts])
        setCursor(page.nextCursor)
        setHasMore(page.nextCursor !== null)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load more facts')
      })
      .finally(() => setLoadingMore(false))
  }, [workspaceId, cursor, hasMore, loadingMore])

  const deleteFact = useCallback(async (factId: string) => {
    setDeleteErrors((e) => { const next = { ...e }; delete next[factId]; return next })
    setDeletingIds((s) => ({ ...s, [factId]: true }))
    try {
      await deleteMemoryFact(workspaceId, factId)
      setFacts((prev) => prev.filter((f) => f.id !== factId))
    } catch (err) {
      setDeleteErrors((e) => ({ ...e, [factId]: err instanceof Error ? err.message : 'Failed to delete' }))
    } finally {
      setDeletingIds((s) => { const next = { ...s }; delete next[factId]; return next })
    }
  }, [workspaceId])

  const purgeAll = useCallback(async (): Promise<{ factCount: number } | null> => {
    setPurging(true)
    setPurgeError(null)
    try {
      const result = await purgeWorkspaceMemory(workspaceId)
      setFacts([])
      setCursor(null)
      setHasMore(false)
      return result
    } catch (err) {
      setPurgeError(err instanceof Error ? err.message : 'Failed to purge workspace memory')
      return null
    } finally {
      setPurging(false)
    }
  }, [workspaceId])

  return {
    facts,
    loading,
    loadingMore,
    hasMore,
    error,
    retry: load,
    loadMore,
    deletingIds,
    deleteErrors,
    deleteFact,
    purging,
    purgeError,
    purgeAll,
  }
}
